import { useEffect, useState } from 'react';
import { useStore, useStoreMap } from 'effector-react';
import type { HSDisplaySetting } from '@types';
import { Collapse } from '@components/Collapse';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedScene } from '@stores/loadedStore';
import { NumInput } from '@components/NumInput';

const items: DropdownItem<HSDisplaySetting['mode']>[] = [
  { id: 0, name: '始终显示' },
  { id: 1, name: '自定义时段显示' },
];

const displayDefault: HSDisplaySetting = {
  mode: 0,
  startAt: 0,
  endAt: 0,
};

function formatDuration(duration: number) {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  return { timeString: `${minutes}分${seconds}秒`, minutes, seconds };
}

export function DisplaySetting() {
  const { hotspot } = useStore($editingHotspot)!;

  const { mode, startAt, endAt } = hotspot.display ?? displayDefault;
  const [videoTime, setVideoTime] = useState(0);
  const materialId = useStoreMap({
    store: $loadedScene,
    keys: [],
    fn: (scene) => scene?.materialId ?? null,
  });
  useEffect(() => {
    if (materialId) {
      const time = Math.round(MainPlayer.get()?.videoTotalTime() ?? 0);
      setVideoTime(time);
      $editingHotspotApi.update((draft) => {
        draft.display ??= { ...displayDefault };
        if (endAt > time) {
          draft.display.endAt = time;
        }
        if (startAt > time) {
          draft.display.startAt = time;
        }
      });
    }
  }, [materialId]);

  const handleTypeChange = (typeId: HSDisplaySetting['mode']) => {
    $editingHotspotApi.update((draft) => {
      draft.display ??= { ...displayDefault };
      draft.display.mode = typeId;
      draft.display.startAt = 0;
      if (typeId === 0) {
        draft.display.endAt = 0;
      } else {
        draft.display.endAt = videoTime;
      }
    });
  };

  const totalTime = formatDuration(videoTime);
  const startTime = formatDuration(startAt);
  const endTime = formatDuration(endAt);

  return (
    <Collapse title="显示时段">
      <Dropdown selected={mode} items={items} onChange={handleTypeChange} />
      {mode === 1 && (
        <>
          <div className="mt-20">{`全景视频总时长：${totalTime.timeString}`}</div>
          <div className="mt-10 flex items-center justify-between">
            <span>在视频</span>
            <NumInput
              className="w-45 justify-center px-5"
              min={0}
              max={9999}
              value={startTime.minutes}
              onChange={(val) => {
                $editingHotspotApi.update((draft) => {
                  draft.display ??= { ...displayDefault };
                  draft.display.startAt = Math.min(
                    val * 60 + startTime.seconds,
                    videoTime
                  );
                  if (draft.display.startAt > draft.display.endAt) {
                    draft.display.endAt = draft.display.startAt;
                  }
                });
              }}
            />
            <span>分</span>
            <NumInput
              className="w-45 justify-center px-5"
              min={0}
              max={59}
              value={startTime.seconds}
              onChange={(val) => {
                $editingHotspotApi.update((draft) => {
                  draft.display ??= { ...displayDefault };
                  draft.display.startAt = Math.min(
                    startTime.minutes * 60 + val,
                    videoTime
                  );
                  if (draft.display.startAt > draft.display.endAt) {
                    draft.display.endAt = draft.display.startAt;
                  }
                });
              }}
            />
            <span>秒显示</span>
          </div>
          <div className="mt-10 flex items-center justify-between">
            <span>在视频</span>
            <NumInput
              className="w-40 justify-center px-5"
              min={0}
              max={9999}
              value={endTime.minutes}
              onChange={(val) => {
                $editingHotspotApi.update((draft) => {
                  draft.display ??= { ...displayDefault };
                  draft.display.endAt = Math.min(
                    val * 60 + endTime.seconds,
                    videoTime
                  );
                  if (draft.display.endAt < draft.display.startAt) {
                    draft.display.startAt = draft.display.endAt;
                  }
                });
              }}
            />
            <span>分</span>
            <NumInput
              className="w-40 justify-center px-5"
              min={0}
              max={59}
              value={endTime.seconds}
              onChange={(val) => {
                $editingHotspotApi.update((draft) => {
                  draft.display ??= { ...displayDefault };
                  draft.display.endAt = Math.min(
                    endTime.minutes * 60 + val,
                    videoTime
                  );
                  if (draft.display.endAt < draft.display.startAt) {
                    draft.display.startAt = draft.display.endAt;
                  }
                });
              }}
            />
            <span>秒隐藏</span>
          </div>
        </>
      )}
    </Collapse>
  );
}

export default DisplaySetting;
